// External
import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import { useI18n } from "Hooks";
import classNames from "classnames";

// Components
import SkeletonLoader from "UIComponents/SkeletonLoader";
import MarkdownView from "UIComponents/MarkdownView";
import Accordian from "UIComponents/Accordian";

// Design System
import { Tooltip } from "@toddle-edu/ds-web";
import { EyeHiddenOutlined, CaretDownFilled } from "@toddle-edu/ds-icons";

// Scss
import classes from "./AIJustification.scss";

const styles = {
  iconStyle: { paddingTop: "3px" },
  accordianHeaderStyle: { padding: "8px 12px" },
  accordianMainContentContainerStyle: { margin: 0 },
};

const renderSkeletonLoader = () => {
  const totalSkeletons = 2;

  return (
    <div className={classes.skeletonContainer}>
      {_.times(totalSkeletons, index => {
        return (
          <div key={index}>
            <SkeletonLoader width={"100%"} height={"14px"} />
            <SkeletonLoader width={"50%"} height={"14px"} />
          </div>
        );
      })}
    </div>
  );
};

const renderMarkdown = justification => {
  return (
    <MarkdownView
      content={justification}
      contentContainerClass={classes.markdownView}
    />
  );
};

const renderGroupedJustification = justifications => {
  return _.map(justifications, (justification, index) => {
    const { title, justificationMsg, responseCriteria } = justification || {};

    return (
      <div key={index} className={classes.group}>
        <div className={classes.groupHeader}>
          <div className="text-label">{title}</div>
          {responseCriteria ? <div className={classes.dot} /> : null}
          <div className={"text-body"}>{responseCriteria}</div>
        </div>

        {renderMarkdown(justificationMsg)}

        {index !== _.size(justifications) - 1 ? (
          <div className={classes.divider} />
        ) : null}
      </div>
    );
  });
};

const AIJustification = ({
  justifications,
  isAIGeneratingResponse,
  aiEvaluationLabel,
  isAIResponseAccepted,
  headerTitle = "ai:ai_grade",
  showAIEvaluationLabelOnLeft = false,
}) => {
  const { t } = useI18n();

  const isGrouped = _.isObject(_.first(justifications));

  const renderJustification = () => {
    if (isAIGeneratingResponse) {
      return renderSkeletonLoader();
    }
    if (isGrouped) {
      return renderGroupedJustification(justifications);
    }
    return _.map(justifications, justification => {
      return renderMarkdown(justification);
    });
  };

  const renderAIEvaluationLabel = () => {
    return (
      <div className={classes.aiEvaluationLabelContainer}>
        <div className={classes.dot} />
        <div className={classNames("text-body", classes.aiEvaluationLabel)}>
          {aiEvaluationLabel}
        </div>
      </div>
    );
  };

  const accordionTitle = (
    <div className={classes.justificationHeader}>
      <div className={classNames("text-body", classes.header)}>
        {t(headerTitle)}
      </div>

      {/* for grouped evaluations, we show it on the left with a separator */}
      {(showAIEvaluationLabelOnLeft || isGrouped) &&
      aiEvaluationLabel &&
      !isAIGeneratingResponse ? (
        <div className={classes.separator}>{renderAIEvaluationLabel()}</div>
      ) : null}
    </div>
  );

  const showEvaluationLabelOnRight =
    !isGrouped &&
    !showAIEvaluationLabelOnLeft &&
    aiEvaluationLabel &&
    !isAIGeneratingResponse;

  return (
    <div className={classes.container}>
      <Accordian
        customTitleComponent={
          <div className={classes.accordionTitle}>
            {accordionTitle}

            {showEvaluationLabelOnRight ? renderAIEvaluationLabel() : null}
            <Tooltip
              dsVersion="2.0"
              tooltip={t("ai:ai_grade_and_justification_is_private_to_you")}
              placement="top"
              showArrow={true}
            >
              <EyeHiddenOutlined
                variant="subtle"
                size={"xx-small"}
                style={styles.iconStyle}
              />
            </Tooltip>
          </div>
        }
        expandedContent={
          <div className={classes.justifications}>{renderJustification()}</div>
        }
        hideContainerBorder={true}
        showLabelBorder={false}
        isExpanded={!isAIResponseAccepted}
        headerStyle={styles.accordianHeaderStyle}
        customRightArrow={
          <CaretDownFilled size={"xx-small"} variant="subtle" />
        }
        mainContentContainerStyle={styles.accordianMainContentContainerStyle}
      />
    </div>
  );
};

AIJustification.propTypes = {
  justifications: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        responseCriteria: PropTypes.string.isRequired,
        justificationMsg: PropTypes.string.isRequired,
      })
    ),
  ]).isRequired,
  isAIGeneratingResponse: PropTypes.bool,
  aiEvaluationLabel: PropTypes.string,
  isAIResponseAccepted: PropTypes.bool,
  showAIEvaluationLabelOnLeft: PropTypes.bool,
};

AIJustification.defaultProps = {
  justifications: [],
  isAIGeneratingResponse: false,
  aiEvaluationLabel: "",
  isAIResponseAccepted: false,
  showAIEvaluationLabelOnLeft: false,
};

export default AIJustification;
